import { useAuth } from "@clerk/clerk-react";
import { IBackendMenuModuleConfig } from "../../types/backend";
import {
  convertAgentBlueprintToBackend,
  convertAgentBlueprintToFrontend,
} from "../converters/agent-blueprint-converter";
import {
  convertMenuConfigToBackend,
  convertMenuConfigToFrontend,
  convertMenuToFrontend,
} from "../converters/menu-converter";
import { IMenuModuleConfig, Menu } from "../../types/menu";
import { IAgent, IAgentBlueprint } from "../../types/base";
import { IOrganization } from "@/components/organization-picker";
import { StripeCustomerResponse } from "@/components/stripe-customer-card";
import { StripeCustomerPost } from "@/components/go-live-dialog";
import { createApiClient, createMenuClient } from "./apiService";

export function useAgentEditorApi() {
  const { getToken } = useAuth();
  const apiClient = createApiClient(getToken);
  const menuClient = createMenuClient();

  // TODO: buy the number on agent create
  const createAgent = async (agent: IAgentBlueprint | null) => {
    if (!agent) {
      throw new Error("Agent is required");
    }
    const agentToSubmit = convertAgentBlueprintToBackend(agent);
    if (!agentToSubmit) {
      throw new Error("Failed to convert agent blueprint to backend");
    }
    agentToSubmit.phoneNumber = undefined;
    agentToSubmit.slug = undefined;
    agentToSubmit.hoursLoman = undefined;
    agentToSubmit.faqs = undefined;
    agentToSubmit.productId = undefined;
    agentToSubmit.subscriptionItemId = undefined;
    agentToSubmit.createdAt = undefined;
    agentToSubmit.updatedAt = undefined;
    agentToSubmit.deletedAt = undefined;
    agentToSubmit.pausedUntil = undefined;
    agentToSubmit.allowAnon = undefined;
    agentToSubmit.panic = undefined;

    const response = await apiClient.post("/admin/agents", agentToSubmit);
    return response.data.id;
  };

  const fetchAgent = async (
    agentId: string,
  ): Promise<IAgentBlueprint | null> => {
    const response = await apiClient.get(`/admin/agents/${agentId}`);
    return convertAgentBlueprintToFrontend(response.data);
  };

  const fetchMenu = async (agentId: string): Promise<Menu | null> => {
    const response = await apiClient.get(`/admin/agents/${agentId}/raw-menu`);
    return convertMenuToFrontend(response.data);
  };

  const updateMenu = async (agentId: string, menu: Menu) => {
    const response = await apiClient.post(
      `/admin/agents/${agentId}/raw-menu`,
      menu,
    );
    return convertMenuToFrontend(response.data);
  };

  const deployAgent = async (agent: IAgentBlueprint) => {
    if (!agent.id) {
      throw new Error("Agent ID is required");
    }

    let shouldCreate = true;
    try {
      const checkProd = await apiClient.get(`/admin/agents/${agent.id}`);
      shouldCreate = !checkProd.status;
    } catch {
      shouldCreate = true;
    }
    if (shouldCreate) {
      const agentBlueprintToDeploy = convertAgentBlueprintToBackend(agent);
      if (!agentBlueprintToDeploy) {
        throw new Error("Failed to convert agent blueprint to backend");
      }
      agentBlueprintToDeploy.slug = undefined;
      agentBlueprintToDeploy.hoursLoman = undefined;
      agentBlueprintToDeploy.faqs = undefined;
      agentBlueprintToDeploy.productId = undefined;
      agentBlueprintToDeploy.subscriptionItemId = undefined;
      agentBlueprintToDeploy.createdAt = undefined;
      agentBlueprintToDeploy.updatedAt = undefined;
      agentBlueprintToDeploy.deletedAt = undefined;
      agentBlueprintToDeploy.pausedUntil = undefined;
      agentBlueprintToDeploy.allowAnon = undefined;
      agentBlueprintToDeploy.panic = undefined;
      await apiClient.post("/admin/agents", agentBlueprintToDeploy);
    } else {
      const agentToDeploy = convertAgentBlueprintToBackend(agent);
      if (!agentToDeploy) {
        throw new Error("Failed to convert agent blueprint to backend");
      }
      await apiClient.patch(`/admin/agents/${agent.id}`, agentToDeploy);
    }
  };

  const rescrapeMenu = async (agentId: string): Promise<void> => {
    await menuClient.post("/scrape", { agentId });
  };

  const initializeChat = async (agentId: string) => {
    const response = await apiClient.post(`/admin/agents/${agentId}/chat`);
    return response.data.callId;
  };

  const requestPhoneNumber = async (
    agentId: string | null,
    data: {
      countryCode: string;
      areaCode: number;
      fallbackPhoneNumber?: string;
    },
  ) => {
    if (!agentId) {
      throw new Error("Agent ID is required");
    }
    const response = await apiClient.post(
      `/admin/agents/${agentId}/phoneNumber`,
      data,
    );
    return response.data.phoneNumber;
    // const random4Digits = Math.floor(1000 + Math.random() * 9000).toString();
    // return `+1781229${random4Digits}`;
  };

  // TODO: is there such a thing as unpublished agents? that live only locally
  const fetchAdminAgents = async () => {
    const response = await apiClient.get("/admin/agents");
    const agents = response.data;
    return agents.sort((a: IAgent, b: IAgent) => a.name.localeCompare(b.name));
  };

  const fetchOrganizations = async (): Promise<IOrganization[]> => {
    const response = await apiClient.get("/admin/accounts");
    return response.data;
  };

  const createOrganization = async (name: string) => {
    const response = await apiClient.post("/admin/accounts", { name });
    return response.data;
  };

  const fetchTimezone = async (latitude: number, longitude: number) => {
    const response = await apiClient.get(
      `/admin/timezone?latitude=${latitude}&longitude=${longitude}`,
    );
    return response.data.timezone;
  };

  const cleanMenu = async (
    agentId: string,
  ): Promise<IMenuModuleConfig | null> => {
    const response = await menuClient.post("/clean", { agentId });
    return convertMenuConfigToFrontend(response.data.menuModuleConfig);
  };

  const previewMenu = async (
    agentId: string,
    menuConfig: IMenuModuleConfig,
  ) => {
    const menuConfigToSubmit = convertMenuConfigToBackend(menuConfig);
    const response = await menuClient.post("/transform/preview", {
      agentId,
      menuConfig: menuConfigToSubmit,
    });
    return convertMenuToFrontend(response.data.menu);
  };

  type ITestCallResponse = {
    callId: string;
    accessToken: string;
  };
  const startTestCall = async (agentId: string): Promise<ITestCallResponse> => {
    const response = await apiClient.post(`/admin/agents/${agentId}/calls`);
    return response.data as ITestCallResponse;
  };

  // admin panic/recover
  const panicAgent = async (agentId: string) => {
    await apiClient.patch(`/admin/agents/${agentId}`, {
      panic: true,
    });
  };
  const recoverAgent = async (agentId: string) => {
    await apiClient.patch(`/admin/agents/${agentId}`, {
      panic: false,
    });
  };
  const panicAllAgents = async () => {
    await apiClient.post("/admin/panic");
  };
  const recoverAllAgents = async () => {
    await apiClient.post("/admin/recover");
  };

  const fetchActiveScrapingStates = async (): Promise<string[]> => {
    const response = await menuClient.get("/scrape");
    return response.data;
  };

  const fetchStripeCustomers = async (
    accountId: string,
  ): Promise<StripeCustomerResponse[]> => {
    const response = await apiClient.get(
      `/admin/accounts/${accountId}/stripe/customers`,
    );
    return response.data;
  };

  const createStripeCustomer = async (
    accountId: string,
    postStripeCustomerPost: StripeCustomerPost,
  ): Promise<StripeCustomerResponse> => {
    const response = await apiClient.post(
      `/admin/accounts/${accountId}/stripe/customers`,
      postStripeCustomerPost,
    );
    return response.data;
  };

  const goLiveAgent = async (
    agentId: string,
    minimumPrice: number,
    pricePerMinute: number,
    pricePerCall: number,
    customerId: string | null,
  ): Promise<{ startedAt: string; subscriptionUrl: string }> => {
    const response = await apiClient.post(`/admin/agents/${agentId}/go-live`, {
      minimumPrice,
      pricePerMinute,
      pricePerCall,
      customerId,
    });
    return response.data;
  };

  const billAccountOnboardingFee = async (
    accountId: string,
    amountInCents: number,
  ): Promise<string> => {
    if (!accountId) {
      throw new Error("Account ID is required");
    }
    if (!amountInCents) {
      throw new Error("Amount in cents is required");
    }
    if (amountInCents <= 0) {
      throw new Error("Amount in cents must be greater than 0");
    }
    const response = await apiClient.post(
      `/admin/accounts/${accountId}/stripe/onboard`,
      {
        amount: amountInCents,
      },
    );
    return response.data.url;
  };

  const deleteAgent = async (agentId: string, reason: string) => {
    const response = await apiClient.delete(
      `/admin/agents/${agentId}?reason=${encodeURIComponent(reason)}`,
    );
    return response.data;
  };

  const impersonateUser = async (userId: string) => {
    const response = await apiClient.get(`/admin/users/${userId}/impersonate`);
    return response.data.url;
  };

  const snapshotMenu = async (agentId: string) => {
    const response = await menuClient.post(`/migrate/snapshot`, { agentId });
    return response.data;
  };

  const migrateStream = async (
    agentId: string,
  ): Promise<IBackendMenuModuleConfig> => {
    const response = await menuClient.post(`/migrate`, { agentId });
    return response.data;
  };

  return {
    createAgent,
    fetchAgent,
    fetchMenu,
    updateMenu,
    deployAgent,
    rescrapeMenu,
    initializeChat,
    requestPhoneNumber,
    fetchAdminAgents,
    fetchOrganizations,
    createOrganization,
    fetchTimezone,
    cleanMenu,
    previewMenu,
    startTestCall,
    panicAgent,
    recoverAgent,
    panicAllAgents,
    recoverAllAgents,
    fetchActiveScrapingStates,
    fetchStripeCustomers,
    createStripeCustomer,
    goLiveAgent,
    billAccountOnboardingFee,
    impersonateUser,
    snapshotMenu,
    migrateStream,
    deleteAgent,
  };
}
